import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import "../../css/splash/capabilities.css";
import useSwipe from "../hooks/useSwipe";
import { CapabilitiesData } from "./SplashData";
import nebulaBg from "../../assets/nebulas/orion-nebula.jpg";

export const Capabilities = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCardClick = (index) => {
    if (isAnimating || index === activeIndex) return;
    setIsAnimating(true);
    setActiveIndex(index);
  };

  const handleDotClick = (index) => {
    if (isAnimating || index === activeIndex) return;
    setIsAnimating(true);
    setActiveIndex(index);
  };

  const handleSwipeLeft = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex((prevIndex) => (prevIndex + 1) % CapabilitiesData.length);
  };

  const handleSwipeRight = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? CapabilitiesData.length - 1 : prevIndex - 1
    );
  };

  const swipeHandlers = useSwipe(handleSwipeLeft, handleSwipeRight);

  const getVisibleCards = () => {
    const cards = [];
    const totalCards = CapabilitiesData.length;

    // Previous card (only if not at start)
    if (activeIndex > 0) {
      const prevIndex = activeIndex - 1;
      cards.push({
        ...CapabilitiesData[prevIndex],
        position: "prev",
        className: "card prev",
      });
    }

    // Active card
    cards.push({
      ...CapabilitiesData[activeIndex],
      position: "active",
      className: "card active",
    });

    // Next card (only if not at end)
    if (activeIndex < totalCards - 1) {
      const nextIndex = activeIndex + 1;
      cards.push({
        ...CapabilitiesData[nextIndex],
        position: "next",
        className: "card next",
      });
    }

    return cards;
  };

  const handlePrevClick = () => {
    if (activeIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleNextClick = () => {
    if (activeIndex < CapabilitiesData.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setActiveIndex(activeIndex + 1);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [activeIndex]);

  const parallaxOffset = scrollY * -0.3;

  return (
    <section 
      className="capabilities-section" 
      style={{ 
        backgroundImage: `url(${nebulaBg})`,
        backgroundPositionY: `${parallaxOffset}px`
      }}
    >
      <h2 className="section-title">Our Core Capabilities</h2>

      <div className="cards-container" {...swipeHandlers}>
        {activeIndex > 0 && (
          <button
            className="nav-arrow nav-arrow-left"
            onClick={handlePrevClick}
            aria-label="Previous capability"
          >
            <ChevronDown size={48} strokeWidth={2.5} />
          </button>
        )}

        {getVisibleCards().map((card) => (
          <div
            key={card.id}
            className={card.className}
          >
            <div className="card-content">
              <span className="card-action">{card.action}</span>
              <h3 className="card-title">{card.title}</h3>
              <p className="card-description">{card.text}</p>
              {card.position === "active" && (
                <Link to={`${card.linkPath}#${card.linkID}`} className="card-link">
                  Learn More
                </Link>
              )}
            </div>
          </div>
        ))}

        {activeIndex < CapabilitiesData.length - 1 && (
          <button
            className="nav-arrow nav-arrow-right"
            onClick={handleNextClick}
            aria-label="Next capability"
          >
            <ChevronDown size={48} strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="dot-indicators">
        {CapabilitiesData.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === activeIndex ? "active" : ""}`}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to capability ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};
